/**
 * Shopify Wishlist Manager - MVP Version
 * Simple wishlist functionality using local storage
 */

(function() {
  'use strict';

  // Constants
  const STORAGE_KEY = 'shopify_wishlist';
  const MAX_ITEMS = 100;

  // Main WishlistManager object
  const WishlistManager = {

    // Initialize
    init: function() {
      if (!this.checkLocalStorage()) {
        console.warn('LocalStorage not available');
        return;
      }

      this.initializeStorage();
      this.bindEvents();
      this.updateButtonStates();
      this.updateCounterBadges();
    },

    // Check if localStorage is available
    checkLocalStorage: function() {
      try {
        const test = '__test__';
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
      } catch (e) {
        return false;
      }
    },

    // Initialize storage
    initializeStorage: function() {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ items: [] }));
      }
    },

    // Bind events
    bindEvents: function() {
      document.addEventListener('click', (e) => {
        const button = e.target.closest('.wishlist-button');
        if (button) {
          e.preventDefault();
          this.handleButtonClick(button);
        }
      });

      // Sync across tabs
      window.addEventListener('storage', (e) => {
        if (e.key === STORAGE_KEY) {
          this.updateButtonStates();
          this.updateCounterBadges();
        }
      });
    },

    // Handle button click
    handleButtonClick: function(button) {
      const productData = {
        id: button.dataset.productId,
        handle: button.dataset.productHandle,
        title: button.dataset.productTitle,
        price: button.dataset.productPrice,
        compare_at_price_min: button.dataset.productCompareAtPriceMin ? parseInt(button.dataset.productCompareAtPriceMin, 10) : null,
        price_min: button.dataset.productPriceMin ? parseInt(button.dataset.productPriceMin, 10) : null,
        image: button.dataset.productImage,
        url: `/products/${button.dataset.productHandle}`
      };

      if (!productData.id) return;

      const isInWishlist = this.isInWishlist(productData.id);

      // Add animation class
      button.classList.add(isInWishlist ? 'wishlist-button--removing' : 'wishlist-button--adding');

      setTimeout(() => {
        if (isInWishlist) {
          this.removeProduct(productData.id);
          this.showToast(`${productData.title} entfernt`);
        } else {
          this.addProduct(productData);
          this.showToast(`${productData.title} hinzugefügt`);
        }

        // Add success animation
        button.classList.remove('wishlist-button--adding', 'wishlist-button--removing');
        button.classList.add('wishlist-button--success');

        setTimeout(() => {
          button.classList.remove('wishlist-button--success');
        }, 600);

        this.updateButtonStates();
        this.updateCounterBadges();
      }, 200);
    },

    // Add product
    addProduct: function(product) {
      const data = this.getData();
      if (data.items.length >= MAX_ITEMS) {
        this.showToast('Merkliste ist voll');
        return false;
      }

      if (this.isInWishlist(product.id)) {
        return false;
      }

      data.items.push(product);
      this.saveData(data);

      // GTM dataLayer tracking
      if (window.dataLayer) {
        pushEventToDataLayer("wishList:addProduct","", window.location.href, "");
      }

      return true;
    },

    // Remove product
    removeProduct: function(productId) {
      const data = this.getData();
      // Find product before removing to get details for tracking
      const product = data.items.find(item => item.id === productId);

      data.items = data.items.filter(item => item.id !== productId);
      this.saveData(data);

      // GTM dataLayer tracking
      if (window.dataLayer && product) {
        pushEventToDataLayer("wishList:removeProduct", "", window.location.href, "");

      }

      return true;
    },

    // Check if product is in wishlist
    isInWishlist: function(productId) {
      const data = this.getData();
      return data.items.some(item => item.id === productId);
    },

    // Get wishlist
    getWishlist: function() {
      return this.getData().items;
    },

    // Get count
    getWishlistCount: function() {
      return this.getData().items.length;
    },

    // Clear wishlist
    clearWishlist: function() {
      this.saveData({ items: [] });
      return true;
    },

    // Get data from storage
    getData: function() {
      try {
        const data = localStorage.getItem(STORAGE_KEY);
        return data ? JSON.parse(data) : { items: [] };
      } catch (e) {
        console.error('Error reading wishlist:', e);
        return { items: [] };
      }
    },

    // Save data to storage
    saveData: function(data) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (e) {
        console.error('Error saving wishlist:', e);
      }
    },

    // Update button states
    updateButtonStates: function() {
      document.querySelectorAll('.wishlist-button').forEach(button => {
        const productId = button.dataset.productId;
        if (productId) {
          const isInWishlist = this.isInWishlist(productId);
          const wasInWishlist = button.classList.contains('wishlist-button--filled');

          button.classList.toggle('wishlist-button--filled', isInWishlist);
          button.classList.toggle('wishlist-button--empty', !isInWishlist);
          button.setAttribute('aria-pressed', isInWishlist);

        }
      });
    },

    // Update counter badges
    updateCounterBadges: function() {
      const count = this.getWishlistCount();
      document.querySelectorAll('.wishlist-counter-badge').forEach(badge => {
        const wasVisible = badge.style.display !== 'none' && badge.textContent !== '';

        badge.textContent = count;
        badge.classList.toggle('wishlist-counter-badge--hidden', count === 0);
        badge.style.display = count > 0 ? 'block' : 'none';

        // Add update animation if badge was already visible
        if (wasVisible && count > 0) {
          badge.classList.add('wishlist-counter-badge--updating');
          setTimeout(() => {
            badge.classList.remove('wishlist-counter-badge--updating');
          }, 400);
        }
      });
    },

    // Show toast notification
    showToast: function(message) {
      // Remove existing toast
      const existing = document.querySelector('.wishlist-toast');
      if (existing) {
        existing.classList.remove('wishlist-toast--visible');
        setTimeout(() => existing.remove(), 300);
      }

      // Create toast
      const toast = document.createElement('div');
      toast.className = 'wishlist-toast';

      toast.innerHTML = `
        <div class="wishlist-toast__content">
          <span class="wishlist-toast__message">${message}</span>
        </div>
      `;

      // Set variant class by message content
      const _m = (message || '').toLowerCase();
      if (_m.includes('hinzugefügt')) {
        toast.classList.add('wishlist-toast--success');
      } else if (_m.includes('entfernt')) {
        toast.classList.add('wishlist-toast--error');
      } else if (_m.includes('voll')) {
        toast.classList.add('wishlist-toast--warning');
      }

      document.body.appendChild(toast);


      // Show toast with animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          toast.classList.add('wishlist-toast--visible');
        });
      });

      // Hide and remove toast
      setTimeout(() => {
        toast.classList.remove('wishlist-toast--visible');
        setTimeout(() => toast.remove(), 400);
      }, 3000);
    },

    // Render wishlist page
    renderWishlistPage: function() {
      const loadingEl = document.getElementById('wishlist-loading');
      const emptyEl = document.getElementById('wishlist-empty');
      const itemsEl = document.getElementById('wishlist-items');
      const errorEl = document.getElementById('wishlist-error');
      const counterEl = document.getElementById('wishlist-counter');
      const clearBtn = document.getElementById('clear-wishlist');

      if (!itemsEl) return;

      const wishlist = this.getWishlist();
      const count = wishlist.length;

      // Hide loading
      if (loadingEl) loadingEl.style.display = 'none';

      // Update counter
      if (counterEl) counterEl.textContent = count;

      // Show/hide clear button
      if (clearBtn) {
        clearBtn.style.display = count > 0 ? 'block' : 'none';
        clearBtn.onclick = () => {
          if (confirm('Wunschliste wirklich löschen?')) {
            this.clearWishlist();
            this.renderWishlistPage();
            this.updateButtonStates();
            this.updateCounterBadges();
          }
        };
      }

      if (count === 0) {
        if (emptyEl) emptyEl.style.display = 'block';
        if (itemsEl) itemsEl.style.display = 'none';
        if (errorEl) errorEl.style.display = 'none';
      } else {
        if (emptyEl) emptyEl.style.display = 'none';
        if (errorEl) errorEl.style.display = 'none';
        if (itemsEl) {
          itemsEl.style.display = 'grid';
          this.renderItems(itemsEl, wishlist);
        }
      }
    },

    // Format money helper
    formatMoney: function(cents) {
      if (typeof window.Currency !== 'undefined' && typeof window.Currency.formatMoney === 'function' && typeof window.theme !== 'undefined' && window.theme.moneyFormat) {
        return window.Currency.formatMoney(cents, window.theme.moneyFormat);
      }
      // Fallback formatting
      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    },

    // Render items
    renderItems: function(container, items) {
      container.innerHTML = '';

      items.forEach(item => {
        const itemEl = document.createElement('div');
        itemEl.className = 'wishlist-item';

        // Build price HTML
        let priceHTML = '';
        const regularPrice = item.compare_at_price_min;
        const salePrice = item.price_min;

        if (regularPrice && salePrice && regularPrice > salePrice) {
          // Show both regular price and sale price
          priceHTML = `
            <span class="wishlist-item__regular-price">${this.formatMoney(regularPrice)}</span>
            <span class="wishlist-item__price-separator">·</span>
            <span class="wishlist-item__sale-price">${this.formatMoney(salePrice)}</span>
          `;
        } else if (salePrice) {
          // Show only sale price
          priceHTML = `<span class="wishlist-item__sale-price">${this.formatMoney(salePrice)}</span>`;
        } else {
          // Fallback to existing price format (for backward compatibility)
          priceHTML = `<span class="wishlist-item__price">${item.price || ''}</span>`;
        }

        itemEl.innerHTML = `
          <div class="wishlist-item__image-container">
            ${item.image ? `
              <img src="${item.image}" alt="${item.title}" class="wishlist-item__image" loading="lazy">
            ` : `
              <div class="wishlist-item__placeholder">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21,15 16,10 5,21"/>
                </svg>
              </div>
            `}
          </div>
          <div class="wishlist-item__content">
            <a href="${item.url}" class="wishlist-item__title" onclick="pushEventToDataLayer('wishList:toProductPage', '', window.location.href, '');">${item.title.includes('|') ? item.title.split('|').slice(1).join('|').trim() : item.title}</a>
            <div class="wishlist-item__price">${priceHTML}</div>
            <div class="wishlist-item__actions">
              <a href="${item.url}" class="wishlist-item__view-btn" onclick="pushEventToDataLayer('wishList:toProductPage', '', window.location.href, '');">Ansehen</a>
              <button class="wishlist-item__remove-btn" data-product-id="${item.id}">Nicht mehr merken</button>
            </div>
          </div>
        `;

        // Bind remove button
        const removeBtn = itemEl.querySelector('.wishlist-item__remove-btn');
        removeBtn.onclick = (e) => {
          e.stopPropagation(); // Prevent card click
          this.removeProduct(item.id);
          this.showToast(`${item.title} entfernt`);
          this.renderWishlistPage();
          this.updateButtonStates();
          this.updateCounterBadges();
        };

        // Make the whole card clickable
        itemEl.style.cursor = 'pointer';
        itemEl.onclick = (e) => {
          // Don't navigate if clicking on remove button or links
          if (e.target.closest('.wishlist-item__remove') || e.target.closest('.wishlist-item__view-btn') || e.target.closest('.wishlist-item__title')) {
            return;
          }
          window.location.href = item.url;
        };

        // Prevent default link behavior for title - let card handle it
        const titleLink = itemEl.querySelector('.wishlist-item__title');
        titleLink.addEventListener('click', (e) => {
          e.preventDefault();
          window.location.href = item.url;
        });

        container.appendChild(itemEl);
      });
    }
  };

  // Make globally available
  window.WishlistManager = WishlistManager;

  // Auto-initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => WishlistManager.init());
  } else {
    WishlistManager.init();
  }

})();
